import type { ReportQuery } from '../../services/report-query';
import type { DashboardGraphQuery } from '../templates/dashboard-graph-widget/dashboard-graph-widget';
import type { DashboardProgressMetric } from '../templates/dashboard-welcome-widget/dashboard-welcome-widget';
import type { DashboardWidgetDefinition } from '../widget-and-graphs.models';

/** Explicit data ownership, including widgets whose specialised component builds its queries. */
export type DashboardWidgetQuery =
  | { kind: 'single'; query: ReportQuery; subtextQuery?: ReportQuery }
  | { kind: 'graph'; valueQuery: ReportQuery; graphQueries: DashboardGraphQuery[] }
  | { kind: 'metrics'; metrics: DashboardProgressMetric[] }
  | { kind: 'component'; owner: 'finance-money' | 'finance-cashflow' }
  | { kind: 'context'; input: 'value' };

export interface DashboardWidgetDisplayInputs extends Record<string, unknown> {
  query?: never;
  valueQuery?: never;
  graphQueries?: never;
  subtextQuery?: never;
  metrics?: never;
}

/** Authored widget object: every widget declares its data strategy and rendering contract. */
export interface DashboardWidgetConfiguration extends Omit<DashboardWidgetDefinition, 'inputs'> {
  inputs: DashboardWidgetDisplayInputs;
  /** Paste an optional static SVG to replace the template icon. */
  iconSvg?: string;
  query: DashboardWidgetQuery;
}

/** UI composition: bind typed data configuration to the existing component input contracts. */
export function createWidgetDefinition(configuration: DashboardWidgetConfiguration): DashboardWidgetDefinition {
  const { query, iconSvg, ...definition } = configuration;
  let queryInputs: Record<string, unknown>;
  switch (query.kind) {
    case 'single':
      queryInputs = { query: query.query, ...(query.subtextQuery ? { subtextQuery: query.subtextQuery } : {}) };
      break;
    case 'graph':
      queryInputs = { valueQuery: query.valueQuery, graphQueries: query.graphQueries };
      break;
    case 'metrics':
      queryInputs = { metrics: query.metrics };
      break;
    case 'component':
    case 'context':
      queryInputs = {};
      break;
  }
  return { ...definition, inputs: { ...definition.inputs, ...queryInputs, ...(iconSvg ? { iconSvg } : {}) } };
}
